import { useEffect, useMemo, useState } from "react";

import { SearchMessageHit } from "./searchApi";
import { SearchResultsList } from "./SearchResultsList";
import { useMessageSearch } from "./useMessageSearch";
import { IconClose, IconSearch } from "../chats/chatIcons";

type SearchScope = "chat" | "global";

export interface SearchPanelProps {
  activeChatID: string;
  onOpenResult: (hit: SearchMessageHit) => Promise<void>;
  onClose?: () => void;
}

export function deriveSearchScopeChatID(scope: SearchScope, activeChatID: string): string | null {
  if (scope === "chat" && activeChatID.trim() !== "") {
    return activeChatID;
  }
  return null;
}

export function getSearchPlaceholder(scope: SearchScope): string {
  if (scope === "chat") {
    return "Search in this conversation";
  }
  return "Search across all conversations";
}

export function SearchPanel(props: SearchPanelProps) {
  const [scope, setScope] = useState<SearchScope>(props.activeChatID.trim() === "" ? "global" : "chat");
  const [input, setInput] = useState("");

  useEffect(() => {
    if (props.activeChatID.trim() === "") {
      setScope("global");
    }
  }, [props.activeChatID]);

  const scopedChatID = useMemo(() => deriveSearchScopeChatID(scope, props.activeChatID), [props.activeChatID, scope]);
  const {
    hits,
    loading,
    loadingMore,
    empty,
    error,
    totalHits,
    canLoadMore,
    loadMore,
    reload,
    setChatID,
    setQuery,
  } = useMessageSearch({
    initialChatID: scopedChatID,
    initialQuery: "",
    debounceMs: 280,
    limit: 20,
  });

  useEffect(() => {
    setChatID(scopedChatID);
  }, [scopedChatID, setChatID]);

  useEffect(() => {
    setQuery(input);
  }, [input, setQuery]);

  return (
    <section className="search-panel" data-testid="search-panel">
      <div className="section-row search-panel-head">
        <h2 className="chat-section-title">Search messages</h2>
        {props.onClose !== undefined ? (
          <button className="icon-button icon-button-ghost" type="button" aria-label="Close search" title="Close search" onClick={props.onClose}>
            <IconClose />
          </button>
        ) : null}
      </div>

      <form
        className="stack-form search-panel-form"
        onSubmit={(event) => {
          event.preventDefault();
          void reload();
        }}
      >
        <div className="search-input-row">
          <label className="chat-field search-field-inline">
            <input
              data-testid="search-input"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder={getSearchPlaceholder(scope)}
              autoComplete="off"
            />
          </label>
          <button className="icon-button icon-button-primary" type="submit" aria-label="Search messages" title="Search messages">
            <IconSearch />
          </button>
        </div>

        <label className="search-scope-toggle">
          <input
            data-testid="search-scope-chat-only"
            type="checkbox"
            checked={scope === "chat"}
            disabled={props.activeChatID.trim() === ""}
            onChange={(event) => setScope(event.target.checked ? "chat" : "global")}
          />
          <span>Search only this conversation</span>
        </label>
      </form>

      <SearchResultsList
        hits={hits}
        loading={loading}
        loadingMore={loadingMore}
        empty={empty}
        error={error}
        totalHits={totalHits}
        canLoadMore={canLoadMore}
        onLoadMore={loadMore}
        onOpenResult={props.onOpenResult}
      />
    </section>
  );
}
